import React, { useEffect, useRef, useState } from 'react';
import './list.css';

/**
 * Choose which columns the table shows. Sits on the saved-view row so it reads as a
 * property of the table rather than another filter.
 *
 * Columns opt out with `hideable: false` -- Name identifies the row, so hiding it
 * would leave a table of numbers belonging to nobody.
 */
export default function ColumnPicker({ columns, hidden, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hiddenCount = columns.filter((c) => hidden.has(c.key)).length;

  return (
    <div className="sv-colpick" ref={wrapRef}>
      <button
        type="button"
        className={`sv-colpick__btn ${hiddenCount ? 'is-active' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <i className="bx bx-columns" aria-hidden="true" />
        <span>Columns</span>
        {hiddenCount > 0 && <span className="sv-colpick__count">{hiddenCount} hidden</span>}
      </button>

      {open && (
        <div className="sv-colpick__pop" role="dialog" aria-label="Choose columns">
          <div className="sv-colpick__list">
            {columns.map((col) => {
              const locked = col.hideable === false;
              return (
                <label
                  key={col.key}
                  className={`sv-colpick__item ${locked ? 'is-locked' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="sv-checkbox"
                    checked={!hidden.has(col.key)}
                    disabled={locked}
                    onChange={() => onToggle(col.key)}
                  />
                  <span>{col.header}</span>
                </label>
              );
            })}
          </div>
          <button type="button" className="sv-colpick__reset" onClick={onReset}>
            Reset to default
          </button>
        </div>
      )}
    </div>
  );
}
